import { useQuery, useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import useBillingLines from '~hooks/useBillingLines/useBillingLines';
import { makeJobsBillingLinesQueryKey } from '~pages/Approvals/Files/utils';
import { makeBillingLineGroupsWithJobsFilters } from '~queries/useBillingLineGroupsQuery';
import { GROUP_BILLING_LINES_QUERY_KEY } from '~queries/useGroupBillingLinesQuery';

import { ChargesSelectionActionsContext } from './ChargesSelectionContext';
import { groupBillingLinesByJob, JobCharges } from './groupBillingLines';

type ChargesBillingLinesQueryContextType = {
  chargesByJobId: Map<string, JobCharges>;
  isError: boolean;
  isLoading: boolean;
  reload: () => void;
};

const ChargesBillingLinesQueryContext =
  createContext<ChargesBillingLinesQueryContextType>({
    chargesByJobId: new Map(),
    isError: false,
    isLoading: false,
    reload: noop,
  });

interface ChargesBillingLinesQueryContextProviderProps {
  companyId: string;
  jobIds: string[];
  orderId: string;
}

function ChargesBillingLinesQueryContextProvider({
  children,
  companyId,
  jobIds,
  orderId,
}: React.PropsWithChildren<ChargesBillingLinesQueryContextProviderProps>) {
  const queryClient = useQueryClient();
  const { fetchAllBillingLinesForJobs } = useBillingLines();
  const { syncCheckedLines } = useContext(ChargesSelectionActionsContext);

  const query = useQuery({
    enabled: jobIds.length > 0,
    queryKey: [makeJobsBillingLinesQueryKey(orderId), companyId, jobIds],
    queryFn: ({ signal }) => fetchAllBillingLinesForJobs(companyId, jobIds, signal),
  });

  useEffect(() => {
    if (query.data && !query.isFetching) {
      syncCheckedLines(query.data, jobIds);
    }
  }, [jobIds, query.data, query.isFetching, syncCheckedLines]);

  const { refetch } = query;
  const reload = useCallback(() => {
    void refetch();
    void queryClient.invalidateQueries(makeBillingLineGroupsWithJobsFilters(jobIds));
    void queryClient.invalidateQueries({ queryKey: [GROUP_BILLING_LINES_QUERY_KEY] });
  }, [jobIds, queryClient, refetch]);

  const value = useMemo(
    () => ({
      chargesByJobId: query.data
        ? groupBillingLinesByJob(query.data)
        : new Map<string, JobCharges>(),
      isError: query.isError,
      isLoading: query.isLoading,
      reload,
    }),
    [query.data, query.isError, query.isLoading, reload],
  );

  return (
    <ChargesBillingLinesQueryContext.Provider value={value}>
      {children}
    </ChargesBillingLinesQueryContext.Provider>
  );
}

export default ChargesBillingLinesQueryContext;
export { ChargesBillingLinesQueryContextProvider };
